import React, { useEffect, useState } from 'react';
import type { SharedArticleItem } from '../types';

const STAGE_OPTIONS = [
  'Подготовка',
  'Отправлено',
  'На рассмотрении',
  'Peer review',
  'Исправления',
  'Редакционный процесс',
  'Принято',
  'Опубликовано / 2026',
] as const;

const STAGE_CODE_BY_LABEL: Record<string, string> = {
  Подготовка: 'PREP',
  Отправлено: 'SUBMITTED',
  'На рассмотрении': 'UNDER_REVIEW',
  'Peer review': 'PEER_REVIEW',
  Исправления: 'REVISIONS',
  'Редакционный процесс': 'EDITORIAL',
  Принято: 'ACCEPTED',
  'Опубликовано / 2026': 'PUBLISHED',
};

interface Props {
  article: SharedArticleItem;
  onClose: () => void;
  onUpdateArticle: (id: string, payload: Partial<SharedArticleItem>) => Promise<void>;
  onOpenOrder: (orderId: string) => void;
  onOpenDiscussion: (article: SharedArticleItem) => void;
  /** When true, render as in-overlay card (template b5 articleEdit). */
  embedded?: boolean;
}

/** Shared article card — template Block5 articleEdit, wired to backend. */
export const SharedArticleDetailModal: React.FC<Props> = ({
  article,
  onClose,
  onUpdateArticle,
  onOpenOrder,
  onOpenDiscussion,
  embedded = true,
}) => {
  const [title, setTitle] = useState(article.title);
  const [journal, setJournal] = useState(article.journal);
  const [stageLabel, setStageLabel] = useState(article.stage_label);
  const [clientSource, setClientSource] = useState(
    article.source_base_name || article.order_id || '',
  );
  const [payment, setPayment] = useState(article.payment_state);
  const [responsible, setResponsible] = useState(article.partner_label);
  const [orderLink, setOrderLink] = useState(article.order_id || '');
  const [notes, setNotes] = useState(article.notes || '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setTitle(article.title);
    setJournal(article.journal);
    setStageLabel(article.stage_label);
    setClientSource(article.source_base_name || article.order_id || '');
    setPayment(article.payment_state);
    setResponsible(article.partner_label);
    setOrderLink(article.order_id || '');
    setNotes(article.notes || '');
  }, [article]);

  const stageSelectOptions = STAGE_OPTIONS.includes(stageLabel as (typeof STAGE_OPTIONS)[number])
    ? STAGE_OPTIONS
    : [stageLabel, ...STAGE_OPTIONS];

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdateArticle(article.id, {
        title: title.trim() || article.title,
        journal: journal.trim() || article.journal,
        stage_label: stageLabel,
        stage_code: STAGE_CODE_BY_LABEL[stageLabel] || article.stage_code,
        partner_label: responsible.trim() || article.partner_label,
        payment_state: payment.trim() || article.payment_state,
        order_id: orderLink.trim() || undefined,
        notes: notes.trim() || null,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const form = (
    <div className="file-storage-article-edit" data-testid="shared-article-detail">
      <h3 style={{ marginTop: 0 }}>
        {article.article_code} · {title || article.title}
      </h3>

      <div className="file-storage-form">
        <div className="file-storage-field wide">
          <label htmlFor="art-title">Название статьи</label>
          <input
            id="art-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="file-storage-field">
          <label htmlFor="art-journal">Журнал / площадка</label>
          <input
            id="art-journal"
            value={journal}
            onChange={(e) => setJournal(e.target.value)}
          />
        </div>

        <div className="file-storage-field">
          <label htmlFor="art-stage">Этап</label>
          <select
            id="art-stage"
            value={stageLabel}
            onChange={(e) => setStageLabel(e.target.value)}
          >
            {stageSelectOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="file-storage-field">
          <label htmlFor="art-client">Клиент / источник заказа</label>
          <input
            id="art-client"
            value={clientSource}
            onChange={(e) => setClientSource(e.target.value)}
            disabled
            title="Источник задаётся реестром; для связи используйте поле заказа ниже"
          />
        </div>

        <div className="file-storage-field">
          <label htmlFor="art-payment">Оплата / финансовый статус</label>
          <input
            id="art-payment"
            value={payment}
            onChange={(e) => setPayment(e.target.value)}
          />
        </div>

        <div className="file-storage-field">
          <label htmlFor="art-resp">Ответственные</label>
          <input
            id="art-resp"
            value={responsible}
            onChange={(e) => setResponsible(e.target.value)}
          />
        </div>

        <div className="file-storage-field">
          <label htmlFor="art-order">Связанный заказ CRM</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              id="art-order"
              value={orderLink}
              onChange={(e) => setOrderLink(e.target.value)}
              placeholder="ID заказа"
              style={{ flex: 1 }}
            />
            {article.order_id ? (
              <button
                type="button"
                className="file-storage-btn"
                onClick={() => {
                  onClose();
                  onOpenOrder(article.order_id!);
                }}
              >
                →
              </button>
            ) : null}
          </div>
        </div>

        <div className="file-storage-field">
          <label>Обновлено</label>
          <input value={article.updated_date || '—'} disabled />
        </div>

        <div className="file-storage-field wide">
          <label htmlFor="art-note">История / примечание</label>
          <textarea
            id="art-note"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Рабочие примечания по рукописи…"
          />
        </div>

        <div className="file-storage-form-actions">
          <button type="button" className="file-storage-btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="file-storage-btn"
            onClick={() => {
              onClose();
              onOpenDiscussion(article);
            }}
          >
            Диалог по статье
          </button>
          <button
            type="button"
            className="file-storage-btn primary"
            disabled={isSaving}
            onClick={() => void handleSave()}
          >
            {isSaving ? 'Сохранение…' : 'Сохранить изменения'}
          </button>
        </div>
      </div>
    </div>
  );

  if (embedded) {
    return (
      <div className="file-storage-wrap">
        <div className="file-storage-breadcrumb">
          Файлы и хранилище / Совместные статьи · <b>карточка</b>
        </div>
        {form}
      </div>
    );
  }

  return (
    <div className="orders-pub-modal-backdrop" onClick={onClose}>
      <div
        className="orders-pub-modal"
        style={{ maxWidth: 920 }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {form}
      </div>
    </div>
  );
};
